import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { userRoutes } from "./roles/user/userRoutes";
import USER_PATHS from "./roles/user/USER_PATHS";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { CloudOff } from "lucide-react";

import {
  checkToken as checkUserToken,
  profileInfo,
  logout as userLogout,
} from "./features/userAuthSlice";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles/toastify-overrides.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import ScrollToTop from "./components/ScrollToTop";
import Preloader from "./components/Preloader";
import Maintenance from "./components/Maintenance";
import CartSyncer from "./components/CartSyncer";
import CookieConsentBanner from "./components/CookieConsentBanner";
import API, { setUnauthorizedHandler } from "./util/API";
import { readAuthSession } from "./util/authSessionStorage";
import { readScopedJson } from "./util/privacyConsent";
import { fetchCurrencyRates } from "./features/currencySlice";
import { fetchProducts } from "./features/productSlice";
import { clearWishlist, fetchWishlist } from "./features/wishlistSlice";
import { setSettingsHydrated, setSettingsSlice, setCountrySource } from "./features/userSettingsSlice";
import { normalizeCurrencyRates } from "./util/money";
import Countries from "./util/Countries";
import countryNames from "./util/countryNames";
import SeoRouter from "./seo/SeoRouter";
import { fetchEnabledCountries } from "./util/countryManagement";
import {
  getDefaultCurrencyForCountry,
  resolveCurrencyForCountry as resolvePolicyCurrencyForCountry,
} from "./util/currencyPolicy";

const SETTINGS_STORAGE_KEY = "user_settings";
const HOME_PREFETCH_LIMIT = 15;
// Splash stays at least this long from first JS evaluation, so a warm cache +
// boot prefetch (products ready ~400 ms) never reads as a sub-second flicker.
const SPLASH_MIN_MS = 500;
const SPLASH_START = Date.now();

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("App crashed:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h1>
            <p className="text-slate-600 mb-6">
              We're sorry, but something went wrong. Please try refreshing the page.
            </p>
            <button
              onClick={this.handleReset}
              className="px-6 py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-[#D2042D] transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const buildEnabledCountryIndex = (countries) => {
  const list = Array.isArray(countries) ? countries : [];
  if (!list.length) return null;
  const codes = new Set();
  const names = new Map();
  const currencies = new Map();
  const byCode = new Map();
  for (const c of list) {
    const code = String(c?.countryCode || "").trim().toUpperCase();
    if (!code) continue;
    codes.add(code);
    byCode.set(code, { ...c, countryCode: code });
    const name = String(c?.countryName || "").trim().toLowerCase();
    if (name) names.set(code, name);
    const currency = String(c?.currencyCode || c?.currency || "").trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(currency)) currencies.set(code, currency);
  }
  return { codes, names, currencies, byCode };
};

const normalizeCountry = (value, enabledIndex = null) => {
  const raw = String(value || "").trim();
  if (!raw) return "US";
  const c = raw.toUpperCase();
  if (enabledIndex?.codes?.size) {
    const enabledFallback = enabledIndex.codes.has("US")
      ? "US"
      : Array.from(enabledIndex.codes)[0] || "US";

    if (enabledIndex.codes.has(c)) return c;

    const codeMatch = c.match(/\b([A-Z]{2})\b/);
    if (codeMatch && enabledIndex.codes.has(codeMatch[1])) return codeMatch[1];

    const nameLower = raw.toLowerCase();
    for (const [code, name] of enabledIndex.names.entries()) {
      if (name === nameLower) return code;
    }

    return enabledFallback;
  }

  if (Countries.includes(c)) return c;

  const codeMatch = c.match(/\b([A-Z]{2})\b/);
  if (codeMatch && Countries.includes(codeMatch[1])) return codeMatch[1];

  const nameLower = raw.toLowerCase();
  const match = Object.entries(countryNames).find(([, name]) =>
    String(name || "").toLowerCase() === nameLower
  );
  if (match) return match[0];

  return "US";
};

const hasVisitorCountrySelection = () => {
  try {
    const parsed = readScopedJson(SETTINGS_STORAGE_KEY, null, { persistWhenAccepted: true });
    return parsed?.userOverride === true;
  } catch {
    return false;
  }
};

const readStoredSettings = () => {
  try {
    const parsed = readScopedJson(SETTINGS_STORAGE_KEY, null, { persistWhenAccepted: true });
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const normalizeCurrency = (value) => {
  const cur = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(cur) ? cur : "";
};

// Membership test for a currency code against the /settings/currency payload.
// The payload shape varies (currency-keyed rates, country-keyed rates, or a
// countryCurrencies map), so we accept any of them as proof the code is live.
// When no rate information exists at all we trust the caller's code — the geo
// endpoint already validates currencies against rates server-side.
const currencyInRates = (currencyPayload, code) => {
  const cur = normalizeCurrency(code);
  if (!cur) return false;
  if (cur === "USD") return true;
  if (!currencyPayload || typeof currencyPayload !== "object") return true;

  const rates = normalizeCurrencyRates(currencyPayload);
  if (Object.keys(rates).length > 1) return Number(rates[cur]) > 0;

  const byCountry =
    currencyPayload?.countryCurrencies && typeof currencyPayload.countryCurrencies === "object"
      ? currencyPayload.countryCurrencies
      : currencyPayload?.rates && typeof currencyPayload.rates === "object"
        ? currencyPayload.rates
        : null;
  if (byCountry && Object.keys(byCountry).length) {
    return Object.values(byCountry).some((v) =>
      normalizeCurrency(typeof v === "string" ? v : v?.currency || v?.currencyCode || v?.code) === cur
    );
  }

  return true;
};

const inferCurrencyForCountry = (currencyPayload, country, enabledIndex = null) => {
  const cc = normalizeCountry(country, enabledIndex);
  const configuredCountry = enabledIndex?.byCode?.get(cc);
  if (configuredCountry) return getDefaultCurrencyForCountry(configuredCountry);

  const configuredCurrency = enabledIndex?.currencies?.get(cc);
  if (configuredCurrency) {
    return cc === "US" || cc === "BD" ? "USD" : configuredCurrency;
  }

  const rates = currencyPayload?.rates;
  if (rates && typeof rates === "object") {
    const byCountry = rates[cc];
    const countryCurrency = normalizeCurrency(
      byCountry?.currency || byCountry?.currencyCode || byCountry?.code
    );
    if (countryCurrency) return cc === "US" || cc === "BD" ? "USD" : countryCurrency;
  }

  // Country-keyed map shipped by some /settings/currency payloads
  // (same shape SettingsPopup consults). US/BD stores price in USD by policy.
  const byCountryLegacy = currencyPayload?.countryCurrencies;
  const legacyCurrency = normalizeCurrency(byCountryLegacy?.[cc]);
  if (legacyCurrency) return cc === "US" || cc === "BD" ? "USD" : legacyCurrency;

  return "USD";
};

const resolveCountryCurrency = (
  country,
  preferredCurrency,
  currencyPayload,
  enabledIndex = null,
  { allowDisplayPreference = false } = {}
) => {
  const cc = normalizeCountry(country, enabledIndex);
  const configuredCountry = enabledIndex?.byCode?.get(cc);
  if (configuredCountry) {
    return resolvePolicyCurrencyForCountry(configuredCountry, preferredCurrency, {
      allowCandidate: Boolean(allowDisplayPreference),
    });
  }

  const countryCurrency = inferCurrencyForCountry(currencyPayload, cc, enabledIndex);
  const preferred = normalizeCurrency(preferredCurrency);

  if (!preferred) return countryCurrency;
  if (allowDisplayPreference && preferred === "USD") return preferred;
  // Fix (audit 4-FE): the preferred currency (geo endpoint, which now
  // policy-checks rates server-side, or a stored visitor selection) must not
  // be silently discarded just because it differs from the inferred country
  // default. It wins unless we have rate data that does NOT carry that code,
  // in which case the inferred country currency is the safe answer.
  if (
    countryCurrency &&
    preferred !== countryCurrency &&
    !currencyInRates(currencyPayload, preferred)
  ) {
    return countryCurrency;
  }
  return preferred;
};

// Auth-landing fast path: these routes only need health + enabled countries +
// the persisted market. Geo detection, currency rates and the product prefetch
// are deferred to the first navigation away from the auth pages (see the
// bootstrappedLiteRef/fullBootstrapRef effect in App). '/auth/success' is NOT
// lite: it boots fully and navigates on to a real route right away.
const AUTH_LITE_ROUTES = [USER_PATHS.LOGIN, USER_PATHS.REGISTER, USER_PATHS.FORGOT_PASSWORD];
const AUTH_PAGE_ROUTES = [...AUTH_LITE_ROUTES, "/auth/success"];
const matchesRoutePrefix = (pathname, routes) => {
  const lower = String(pathname || "").toLowerCase();
  return routes.some((route) => lower.startsWith(route));
};
const isAuthLitePath = (pathname) => matchesRoutePrefix(pathname, AUTH_LITE_ROUTES);
const isAuthPagePath = (pathname) => matchesRoutePrefix(pathname, AUTH_PAGE_ROUTES);

const marketFromStoredSettings = (stored, enabledIndex) => {
  const country = normalizeCountry(stored?.shipToCountry || "US", enabledIndex);
  const currency = resolveCountryCurrency(country, stored?.currency, null, enabledIndex);
  return { country, currency };
};

const fetchGeoCountryFallback = async () => {
  try {
    const res = await API.get("/settings/geo/lookup", {
      headers: { Authorization: "" },
    });
    return res?.data?.country || null;
  } catch {
    return null;
  }
};

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAppRedirectRoute = location.pathname.replace(/\/+$/, "") === "/app";
  const [loading, setLoading] = useState(true);
  // Auth-landing routes skip the splash floor entirely (instant form); every
  // other route keeps the original 500 ms minimum.
  const [splashReady, setSplashReady] = useState(
    () => isAuthLitePath(location.pathname) || Date.now() - SPLASH_START >= SPLASH_MIN_MS
  );
  useEffect(() => {
    if (splashReady) return undefined;
    const splashFloor = isAuthLitePath(location.pathname) ? 0 : SPLASH_MIN_MS;
    const t = setTimeout(() => setSplashReady(true), Math.max(0, splashFloor - (Date.now() - SPLASH_START)));
    return () => clearTimeout(t);
  }, [splashReady, location.pathname]);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [bootAttempt, setBootAttempt] = useState(0);
  const [bootMode, setBootMode] = useState("down");
  const [outage, setOutage] = useState(false);
  const outageFailCountRef = useRef(0);
  const outageSuppressedRef = useRef(false);
  const preloadedProductsKeyRef = useRef(null);
  // Auth-lite boot state: when the chain ran in lite mode the geo/currency
  // phase is still owed; fullBootstrapRef flips true once it has run (either
  // inline in a full boot or deferred after navigating away from auth pages).
  const bootstrappedLiteRef = useRef(false);
  const fullBootstrapRef = useRef(true);
  const enabledIndexRef = useRef(null);
  const storedSettingsRef = useRef(null);
  const currentPathRef = useRef(location.pathname);
  currentPathRef.current = location.pathname;

  // Smooth route transitions: keep old route rendered briefly for exit animation.
  const locationKey = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.pathname, location.search, location.hash]
  );
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("enter");

  const shipToCountry = useSelector((state) => state?.userSettings?.shipToCountry) || "US";
  const displayCurrency =
    useSelector((state) => state?.userSettings?.currency) ||
    "USD";
  const authToken = useSelector((state) => state?.userAuth?.token) || null;
  const lastWishlistTokenRef = useRef(null);

  const checkApiStatus = async () => {
    try {
      const res = await API.get("/", {
        headers: { Authorization: "" },
      });
      return res.data?.success === true;
    } catch {
      return false;
    }
  };

  // Shared geo + currency-rates phase of the full bootstrap. Runs inline for
  // normal routes and deferred (once) after an auth-lite boot when the visitor
  // navigates away from the auth pages. Dispatches exactly as before.
  const runMarketBootstrap = useCallback(
    async (stored, enabledIndex) => {
      const base = marketFromStoredSettings(stored, enabledIndex);
      let effectiveCountry = base.country;
      let effectiveCurrency = base.currency;

      if (!hasVisitorCountrySelection()) {
        // Boot keeps a single parallel phase: geo + rates. The geo call gets
        // exactly ONE retry, chained into the same allSettled promise, and
        // only when the first attempt REJECTED with a network error or 5xx.
        // This avoids a serialized 4th phase so the bootstrap watchdog stays
        // an honest bound on total bootstrap time.
        const fetchGeoCountry = () => API.get("/settings/geo/country");
        const [geo, cur] = await Promise.allSettled([
          fetchGeoCountry().catch(async (err) => {
            const status = Number(err?.response?.status);
            const retriable = !err?.response || (status >= 500 && status <= 599);
            if (!retriable) throw err;
            await new Promise((resolve) => setTimeout(resolve, 300));
            return fetchGeoCountry();
          }),
          dispatch(fetchCurrencyRates()).unwrap(),
        ]);

        // Contract (new shape): { success, country, currency, source:
        //   'mmdb'|'cache'|'default'|'fallback-disabled'|'no-enabled',
        //   detectedCountry? }. Old shape lacks source/detectedCountry.
        const geoData = geo.status === "fulfilled" ? geo.value?.data || null : null;
        const geoRaw = geoData?.country || null;
        const geoCurrencyRaw = geoData?.currency || null;
        const geoSource = String(geoData?.source || "").toLowerCase() || null;

        // An answer is authoritative when the primary fulfilled with a
        // detection-derived source, OR (legacy shape) carried a country at
        // all. A genuine US answer must NEVER be overwritten by the weaker
        // lookup endpoint.
        const trustedPrimary =
          geo.status === "fulfilled" &&
          (geoSource === "mmdb" ||
            geoSource === "cache" ||
            geoSource === "fallback-disabled" ||
            (geoSource === null && Boolean(geoRaw)));

        let geoCountry;
        let geoFinalSource;
        if (trustedPrimary) {
          geoCountry = normalizeCountry(geoRaw, enabledIndex);
          geoFinalSource = geoSource;
        } else {
          // Lookup fires ONLY when: the primary rejected twice, or it
          // fulfilled with a non-authoritative source ('default' /
          // 'no-enabled'), or the legacy shape carried no country.
          const lookupRaw = await fetchGeoCountryFallback();
          if (lookupRaw) {
            geoCountry = normalizeCountry(lookupRaw, enabledIndex);
            geoFinalSource = "lookup";
          } else if (geoSource === "default" || geoSource === "no-enabled") {
            // Keep the server's default answer; source explains why.
            geoCountry = normalizeCountry(geoRaw || "", enabledIndex);
            geoFinalSource = geoSource;
          } else {
            geoCountry = normalizeCountry(geoRaw || "", enabledIndex);
            geoFinalSource = "lookup-failed";
          }
        }

        const currencyPayload = cur.status === "fulfilled" ? cur.value : null;
        // The geo endpoint's currency wins: it policy-checks against enabled
        // rates server-side. We only distrust it when we hold rate data that
        // provably lacks that code; then fall back to the returned country's
        // default/inferred currency (works for 'fallback-disabled' and
        // 'no-enabled' too, since we resolve for the RETURNED country).
        const geoPreferredCurrency = normalizeCurrency(geoCurrencyRaw);
        const geoCurrency =
          geoPreferredCurrency && currencyInRates(currencyPayload, geoPreferredCurrency)
            ? geoPreferredCurrency
            : resolveCountryCurrency(geoCountry, null, currencyPayload, enabledIndex);

        effectiveCountry = geoCountry;
        effectiveCurrency = geoCurrency;

        dispatch(
          setSettingsSlice({
            shipToCountry: geoCountry,
            currency: geoCurrency,
            userOverride: false,
          })
        );
        // Non-blocking provenance for the SettingsPopup notice (never
        // persisted, never feeds override logic).
        dispatch(
          setCountrySource({
            source: geoFinalSource,
            detectedCountry: geoData?.detectedCountry || null,
          })
        );
        return { country: effectiveCountry, currency: effectiveCurrency };
      }

      let currencyPayload = null;
      try {
        currencyPayload = await dispatch(fetchCurrencyRates()).unwrap();
      } catch {
        currencyPayload = null;
      }

      effectiveCurrency = resolveCountryCurrency(
        effectiveCountry,
        stored?.currency || effectiveCurrency,
        currencyPayload,
        enabledIndex,
        { allowDisplayPreference: true }
      );
      dispatch(
        setSettingsSlice({
          shipToCountry: effectiveCountry,
          currency: effectiveCurrency,
          userOverride: true,
        })
      );
      return { country: effectiveCountry, currency: effectiveCurrency };
    },
    [dispatch]
  );

  useEffect(() => {
    setUnauthorizedHandler(() => {
      dispatch(userLogout());
      // Session-expired must land on the login form, not a half-logged-out
      // page: same pattern as the manual logout flows. Auth pages already
      // render login-equivalent UI, so skip the redirect there.
      if (!isAuthPagePath(currentPathRef.current)) {
        navigate(USER_PATHS.LOGIN, { replace: true, state: { loggedOut: true } });
      }
    });
  }, [dispatch, navigate]);

  useEffect(() => {
    let cancelled = false;
    let settled = false;

    const init = async () => {
      setLoading(true);

      if (isAppRedirectRoute) {
        if (!cancelled) {
          settled = true;
          setBootstrapped(true);
          setLoading(false);
        }
        return;
      }

      const ok = await checkApiStatus();
      if (!ok) {
        if (!cancelled) {
          settled = true;
          setBootMode(
            typeof navigator !== "undefined" && navigator.onLine === false
              ? "offline"
              : "down"
          );
          setLoading(false);
        }
        return;
      }

      // Fire auth restoration in background (not required to show homepage)
      dispatch(checkUserToken());
      const userToken = readAuthSession().token;
      if (userToken) {
        dispatch(profileInfo());
      }

      // First visit: detect visitor country before initial data preload.
      const stored = readStoredSettings();
      storedSettingsRef.current = stored;
      let enabledIndex = null;
      try {
        const enabledCountries = await fetchEnabledCountries();
        enabledIndex = buildEnabledCountryIndex(enabledCountries);
      } catch {
        enabledIndex = null;
      }
      enabledIndexRef.current = enabledIndex;

      if (isAuthLitePath(currentPathRef.current)) {
        // Auth-lite: the form only needs the persisted market. Geo, currency
        // rates and the product prefetch are deferred to the fullBootstrapRef
        // effect below; MainPage fetches its own rows, so prefetch is skipped
        // for good once we navigate away.
        const market = marketFromStoredSettings(stored, enabledIndex);
        dispatch(
          setSettingsSlice({
            shipToCountry: market.country,
            currency: market.currency,
            userOverride: hasVisitorCountrySelection(),
          })
        );
        preloadedProductsKeyRef.current = `${market.country}|${market.currency}`;
        bootstrappedLiteRef.current = true;
        fullBootstrapRef.current = false;
      } else {
        bootstrappedLiteRef.current = false;
        fullBootstrapRef.current = true;
        const market = await runMarketBootstrap(stored, enabledIndex);

        // The storefront owns its catalog request; avoid blocking it on a duplicate prefetch.
        if (currentPathRef.current !== "/") dispatch(
          fetchProducts({
            country: market.country,
            currency: market.currency,
            page: 1,
            limit: HOME_PREFETCH_LIMIT,
            append: false,
          })
        );

        preloadedProductsKeyRef.current = `${market.country}|${market.currency}`;
      }

      if (!cancelled) {
        settled = true;
        dispatch(setSettingsHydrated(true));
        setBootstrapped(true);
        setLoading(false);
      }
    };

    init();

    // Never strand visitors behind an endless preloader if an endpoint hangs.
    const watchdog = setTimeout(() => {
      if (!cancelled && !settled) {
        settled = true;
        setLoading(false);
      }
    }, 15000);

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
    };
  }, [dispatch, isAppRedirectRoute, bootAttempt, runMarketBootstrap]);

  useEffect(() => {
    if (loading || bootstrapped) return undefined;
    const goOnline = () => {
      setLoading(true);
      setBootAttempt((a) => a + 1);
    };
    const goOffline = () => setBootMode("offline");
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // Auto-recover: keep probing so the screen heals itself when Cherrfy is back.
    const retryInterval = setInterval(async () => {
      const ok = await checkApiStatus();
      if (ok) {
        setLoading(true);
        setBootAttempt((a) => a + 1);
      }
    }, 15000);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(retryInterval);
    };
  }, [loading, bootstrapped]);

  useEffect(() => {
    if (!bootstrapped || isAppRedirectRoute) return undefined;

    let cancelled = false;
    const probe = async () => {
      const ok = await checkApiStatus();
      if (cancelled) return;
      if (ok) {
        outageFailCountRef.current = 0;
        outageSuppressedRef.current = false;
        setOutage(false);
        return;
      }
      outageFailCountRef.current += 1;
      if (outageFailCountRef.current >= 2 && !outageSuppressedRef.current) {
        setOutage(true);
      }
    };

    const interval = setInterval(probe, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [bootstrapped, isAppRedirectRoute]);

  const retryOutage = async () => {
    const ok = await checkApiStatus();
    if (ok) {
      outageFailCountRef.current = 0;
      outageSuppressedRef.current = false;
      setOutage(false);
    }
  };

  const dismissOutage = () => {
    outageSuppressedRef.current = true;
    setOutage(false);
  };

  useEffect(() => {
    const token = String(authToken || "").trim();

    if (!token) {
      lastWishlistTokenRef.current = null;
      dispatch(clearWishlist());
      return;
    }

    if (lastWishlistTokenRef.current === token) return;
    lastWishlistTokenRef.current = token;
    dispatch(fetchWishlist());
  }, [authToken, dispatch]);

  // After bootstrap: if user changes country/currency, refresh products (no global preloader).
  useEffect(() => {
    if (!bootstrapped || currentPathRef.current === "/") return;

    const nextKey = `${shipToCountry}|${displayCurrency}`;
    if (preloadedProductsKeyRef.current === nextKey) {
      // Skip the first run after bootstrap because we already preloaded with the same params.
      preloadedProductsKeyRef.current = null;
      return;
    }

    dispatch(
      fetchProducts({
        country: shipToCountry,
        currency: displayCurrency,
        page: 1,
        limit: HOME_PREFETCH_LIMIT,
        append: false,
      })
    );
  }, [bootstrapped, dispatch, shipToCountry, displayCurrency]);

  // Deferred full bootstrap: after an auth-lite boot the geo + currency-rates
  // phase was skipped. Run it exactly once when the visitor first navigates
  // away from the auth pages. Product prefetch stays skipped (the destination
  // page fetches its own rows; a resulting market change still triggers the
  // key-change refresh effect above).
  useEffect(() => {
    if (!bootstrapped || !bootstrappedLiteRef.current || fullBootstrapRef.current) return undefined;
    if (isAuthLitePath(location.pathname)) return undefined;

    fullBootstrapRef.current = true;
    runMarketBootstrap(storedSettingsRef.current, enabledIndexRef.current).catch(() => {
      // Keep the persisted auth-lite market if the deferred phase fails.
    });
  }, [bootstrapped, location.pathname, runMarketBootstrap]);

  useEffect(() => {
    const displayKey = `${displayLocation.pathname}${displayLocation.search}${displayLocation.hash}`;
    if (locationKey === displayKey) return;

    setTransitionStage("exit");
    const t = setTimeout(() => {
      setDisplayLocation(location);
      setTransitionStage("enter");
    }, 120);

    return () => clearTimeout(t);
  }, [locationKey, location, displayLocation]);

  const renderRoutes = (routes) =>
    routes.map((route, index) => {
      if (route.children) {
        return (
          <Route key={index} path={route.path} element={route.element}>
            {renderRoutes(route.children)}
          </Route>
        );
      }
      return <Route key={index} path={route.path} element={route.element} />;
    });

  if (loading || !splashReady) {
    return <Preloader />;
  }

  if (!bootstrapped) {
    return (
      <Maintenance
        mode={bootMode}
        onRetry={() => {
          setLoading(true);
          setBootAttempt((a) => a + 1);
        }}
      />
    );
  }

  return (
    <ErrorBoundary>
      <>
        <SeoRouter />
        <ScrollToTop pathname={displayLocation.pathname} />
        <CartSyncer />
        <CookieConsentBanner />

        {outage ? (
          <div
            role="status"
            aria-live="polite"
            className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-[1200] w-[min(92vw,540px)] flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/95 backdrop-blur px-4 py-3 shadow-lg"
          >
            <CloudOff size={18} className="shrink-0 text-amber-700" aria-hidden="true" />
            <p className="min-w-0 flex-1 text-xs sm:text-sm font-medium text-amber-900">
              Cherrfy can’t be reached right now. Pages may not update — we’ll
              reconnect automatically.
            </p>
            <button
              type="button"
              onClick={retryOutage}
              className="shrink-0 rounded-lg bg-amber-900 px-3 py-1.5 text-xs font-semibold text-amber-50 hover:bg-black transition-colors"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={dismissOutage}
              aria-label="Dismiss connection notice"
              className="shrink-0 rounded-lg px-2 py-1 text-sm font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
            >
              ×
            </button>
          </div>
        ) : null}

      <ToastContainer
        position="top-right"
        autoClose={3600}
        theme="light"
        closeOnClick={false}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        draggable={false}
        newestOnTop
        limit={4}
        stacked
      />

      <div
        className={`w-full min-w-0 ${
          transitionStage === "exit" ? "ch-route-exit" : "ch-route-enter"
        }`}
      >
    
        <Routes location={displayLocation}>
          {renderRoutes(userRoutes)}
        </Routes>
      </div>
      </>
    </ErrorBoundary>
  );
}

export default App;
